import { useState, type FC } from "react";
import { useMutation } from "@tanstack/react-query";
import type { SxProps, Theme } from "@mui/material/styles";
import { Translate, useTranslation } from "@privacy/translations";
import { acknowledgeOwnCopyMutationOptions, useDeleteRegistration } from "../api/registrations";
import type { RegistrationById } from "../api/registrations.types";
import { usePermission } from "../shared/permissions";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { useRegistrationVariant } from "../pages/RegistrationsGridPage/RegistrationVariantProvider";
import { ConfirmDeleteDialog } from "../shared/ConfirmDeleteDialog";
import { Strong } from "../shared/Strong";
import { DecisionNotice } from "./DecisionNotice";
import { useHasPermissionToChooseResponsible } from "../pages/RegistrationsGridPage/RegistrationDrawer/useHasPermissionToUpdate";

export const OwnCopyNotice: FC<{
  registration: RegistrationById;
  ownCopy: NonNullable<RegistrationById["ownCopy"]>;
  sx?: SxProps<Theme>;
}> = ({ registration, ownCopy, sx }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const hasPermissionToChooseResponsible = useHasPermissionToChooseResponsible();
  const { navigate, paths } = usePrivacyNavigate();
  const variant = useRegistrationVariant();
  const acknowledgeMutation = useMutation(acknowledgeOwnCopyMutationOptions());
  const deleteRegistrationMutation = useDeleteRegistration();
  const [isConfirmingNo, setIsConfirmingNo] = useState(false);
  const canAnswerYes = Boolean(registration.canKeepOwnCopy);
  const canAnswerNo = hasPermission("nova-forms-api:registration:delete", registration.permissions);

  return (
    <>
      <DecisionNotice
        tone="warning"
        sx={sx}
        eyebrow={
          <Translate
            translationKey={`RegistrationGrid.Drawer.OwnCopyWarning.What.${ownCopy.reason}`}
            values={{ customer: <Strong>{ownCopy.byName}</Strong> }}
          />
        }
        question={t("RegistrationGrid.Drawer.OwnCopyWarning.Question")}
        quote={ownCopy.comment}
      >
        {canAnswerYes &&
          (registration.responsible || hasPermissionToChooseResponsible(registration) ? (
            <DecisionNotice.Answer
              answer="yes"
              consequence={t("RegistrationGrid.Drawer.OwnCopyWarning.IfYes", { customer: ownCopy.byName })}
              isPending={acknowledgeMutation.isPending}
              blockedReason={registration.responsible ? undefined : t("SharedProcessing.Claim.ChooseResponsibleFirst")}
              onClick={() => acknowledgeMutation.mutate(registration.id)}
            />
          ) : (
            <DecisionNotice.Line>{t("SharedProcessing.Decision.ResponsibleNeeded")}</DecisionNotice.Line>
          ))}
        {canAnswerNo && (
          <DecisionNotice.Answer
            answer="no"
            consequence={t("RegistrationGrid.Drawer.OwnCopyWarning.IfNo")}
            onClick={() => setIsConfirmingNo(true)}
          />
        )}
        {!canAnswerYes && !canAnswerNo && <DecisionNotice.NoPermission />}
        {(acknowledgeMutation.isError || deleteRegistrationMutation.isError) && <DecisionNotice.Error />}
      </DecisionNotice>
      <ConfirmDeleteDialog
        isOpen={isConfirmingNo}
        onClose={() => setIsConfirmingNo(false)}
        headerText={t("RegistrationGrid.Drawer.OwnCopyWarning.ConfirmRemove")}
        message={
          <Translate
            translationKey="RegistrationGrid.Drawer.OwnCopyWarning.ConfirmMessage"
            values={{ name: <Strong>{registration.name}</Strong> }}
          />
        }
        confirmButtonLabel={t("SharedProcessing.Decision.Delete")}
        onDelete={() =>
          deleteRegistrationMutation.mutateAsync(registration.id, {
            onSuccess: () => navigate(paths.registrationsGrid({ variant })),
          })
        }
      />
    </>
  );
};
